import React, { useState, useEffect } from "react";
import api from "../../../api/axiosConfig";
import Loader from "../../Shared/Loader";
import "../../../css/EditClusterSlotsModal.css";
import OverrideStationModal from "./OverrideStationModal";

interface EditClusterSlotsModalProps {
  station: any;
  onClose: () => void;
}

interface SlotItem {
  _id: string;
  appointment_cluster_id: string;
  center_id: string;
  slot_template_id: string;
  date: string;
  start_time: string;
  end_time: string;
  slot_type: string;
  total_appointments: number;
  available_appointments: number;
  booked_count: number;
  available_derived: number;
}

const EditClusterSlotsModal: React.FC<EditClusterSlotsModalProps> = ({ station, onClose }) => {
  const [slots, setSlots] = useState<SlotItem[]>([]);
  const [configData, setConfigData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [overrideType, setOverrideType] = useState<"day" | "slot">("day");
  const [overrideDate, setOverrideDate] = useState("");
  const [overrideSlot, setOverrideSlot] = useState<any>(null);
  const [overrideStations, setOverrideStations] = useState<any[]>([]);

  const [slotToDelete, setSlotToDelete] = useState<SlotItem | null>(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Date filter state
  const [dateFilter, setDateFilter] = useState<string>("");

  const fetchConfigData = async () => {
    try {
      const token = sessionStorage.getItem("accessToken");
      const configRes = await api.center_api.get<any>(
        `/api/v1/center/${station.centerId}/config?appointmentClusterId=${station.appointmentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (configRes.data && configRes.data.success && configRes.data.data) {
        setConfigData(configRes.data.data);
      }
    } catch (err) {
      console.error("Failed to refresh config", err);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem("accessToken");
      const [slotsRes, configRes] = await Promise.all([
        api.appointment_api.get<{ appointment_cluster_id: string, total: number, slots: SlotItem[] }>(
          `/api/cluster/clusters/${station.appointmentId}/slots?includeBookingCounts=1`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        api.center_api.get<any>(
          `/api/v1/center/${station.centerId}/config?appointmentClusterId=${station.appointmentId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ).catch(() => ({ data: null }))
      ]);

      if (slotsRes.data && slotsRes.data.slots) {
        setSlots(slotsRes.data.slots);
      }
      if (configRes.data && configRes.data.success && configRes.data.data) {
        setConfigData(configRes.data.data);
      }
    } catch (err: any) {
      console.error("Error fetching data:", err);
      setError("Failed to fetch data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [station]);

  const groupedSlots = slots.reduce((acc, slot) => {
    if (!acc[slot.date]) {
      acc[slot.date] = [];
    }
    acc[slot.date].push(slot);
    return acc;
  }, {} as Record<string, SlotItem[]>);

  const filteredDates = Object.keys(groupedSlots)
    .sort()
    .filter(date => !dateFilter || date === dateFilter);

  const getAssignedStaffForSlot = (slot: SlotItem) => {
    if (!configData) return [];

    const baseStations = configData.stations || [];
    const dailyOverride = (configData.dailyOverrides || []).find((ov: any) => ov.date === slot.date);

    let effectiveStations = [...baseStations];

    // Apply daily override if it exists
    if (dailyOverride && dailyOverride.stations) {
      const overrideStationsMap = new Map();
      dailyOverride.stations.forEach((st: any) => {
        overrideStationsMap.set(st.stationId, st);
      });
      effectiveStations = effectiveStations.map((st: any) => {
        if (overrideStationsMap.has(st.stationId)) {
          return overrideStationsMap.get(st.stationId);
        }
        return st;
      });

      // Apply slot overrides if they exist within the day
      if (dailyOverride.slotOverrides && dailyOverride.slotOverrides.length > 0) {
        const slotOverride = dailyOverride.slotOverrides.find(
          (ov: any) => ov.startTime === slot.start_time && ov.endTime === slot.end_time
        );
        if (slotOverride && slotOverride.stations) {
          const slotOverrideStationsMap = new Map();
          slotOverride.stations.forEach((st: any) => {
            slotOverrideStationsMap.set(st.stationId, st);
          });
          effectiveStations = effectiveStations.map((st: any) => {
            if (slotOverrideStationsMap.has(st.stationId)) {
              return slotOverrideStationsMap.get(st.stationId);
            }
            return st;
          });
        }
      }
    }
    return effectiveStations;
  };

  const handleEditDay = (date: string) => {
    const baseStations = configData?.stations || [];
    const dailyOverride = (configData?.dailyOverrides || []).find((ov: any) => ov.date === date);
    let effectiveStations = [...baseStations];
    if (dailyOverride && dailyOverride.stations) {
      const overrideStationsMap = new Map();
      dailyOverride.stations.forEach((st: any) => overrideStationsMap.set(st.stationId, st));
      effectiveStations = effectiveStations.map((st: any) => overrideStationsMap.has(st.stationId) ? overrideStationsMap.get(st.stationId) : st);
    }

    setOverrideStations(effectiveStations);
    setOverrideDate(date);
    setOverrideType("day");
    setOverrideSlot(null);
    setOverrideModalOpen(true);
  };

  const handleEditSlot = (slot: SlotItem) => {
    const effectiveStations = getAssignedStaffForSlot(slot);
    setOverrideStations(effectiveStations);
    setOverrideDate(slot.date);
    setOverrideType("slot");
    setOverrideSlot(slot);
    setOverrideModalOpen(true);
  };

  const handleDeleteClick = (slot: SlotItem) => {
    setSlotToDelete(slot);
    setDeleteReason("");
  };

  const confirmDelete = async () => {
    if (!slotToDelete || !deleteReason.trim()) return;
    setIsDeleting(true);
    try {
      const token = sessionStorage.getItem("accessToken");
      await api.appointment_api.post(
        '/api/released-slots/delete-slots',
        {
          appointment_cluster_id: station.appointmentId,
          slot_ids: [slotToDelete._id],
          updated_by: "Admin",
          reason: deleteReason
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSlotToDelete(null);
      await fetchData();
    } catch (err) {
      console.error("Failed to delete slot", err);
      alert("Failed to delete slot.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="edit-slots-modal-overlay" onClick={onClose}>
      <div className="edit-slots-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="edit-slots-modal-header">
          <h2>Edit Slots for {station.centerName}</h2>
          <button className="edit-slots-close-btn" onClick={onClose}>&times;</button>
        </div>
        
        <div className="edit-slots-filter-container" style={{ padding: '15px 24px', borderBottom: '1px solid #eaeaea', display: 'flex', alignItems: 'center', gap: '15px', backgroundColor: '#fafafa' }}>
          <label htmlFor="slot-date-filter" style={{ fontWeight: 600, color: '#333', margin: 0 }}>Filter by Date:</label>
          <input 
            type="date" 
            id="slot-date-filter"
            value={dateFilter} 
            onChange={(e) => setDateFilter(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px', outline: 'none' }}
          />
          {dateFilter && (
            <button 
              onClick={() => setDateFilter('')}
              style={{ padding: '8px 16px', backgroundColor: '#fff', border: '1px solid #ff4d4f', color: '#ff4d4f', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 500, transition: 'all 0.2s' }}
              onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#ff4d4f'; e.currentTarget.style.color = '#fff'; }}
              onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#ff4d4f'; }}
            >
              Clear Filter
            </button>
          )}
        </div>
        
        {loading ? (
          <Loader text="Loading slots and config..." />
        ) : error ? (
          <p style={{ color: "red", padding: '24px' }}>{error}</p>
        ) : slots.length === 0 ? (
          <p style={{ padding: '24px' }}>No slots found for this cluster.</p>
        ) : filteredDates.length === 0 ? (
          <p style={{ padding: '24px', color: '#666', textAlign: 'center', fontStyle: 'italic' }}>No slots found for the selected date.</p>
        ) : (
          <div className="grouped-slots-container">
            {filteredDates.map(date => (
              <div key={date} className="slot-day-group">
                <div className="slot-day-header">
                  <h3>{date}</h3>
                  <button className="btn-edit-day" onClick={() => handleEditDay(date)}>Edit Day</button>
                </div>
                <div className="slots-grid">
                  {groupedSlots[date].map((slot) => (
                    <div key={slot._id} className="slot-card">
                      <div className="slot-card-header">
                        <span>{slot.start_time} - {slot.end_time}</span>
                      </div>
                      <div className="slot-card-body">
                        <div><strong>Type:</strong> <span>{slot.slot_type}</span></div>
                        <div><strong>Total Appts:</strong> <span>{slot.total_appointments}</span></div>
                        <div><strong>Available:</strong> <span>{slot.available_appointments}</span></div>
                        <div><strong>Booked:</strong> <span>{slot.booked_count}</span></div>
                        
                        <div className="slot-staff-config">
                          <div className="staff-header">Staff Configured</div>
                          <div className="staff-list">
                            {(() => {
                              const staff = getAssignedStaffForSlot(slot);
                              if (!staff || staff.length === 0) return <span style={{ fontSize: '0.8rem', color: '#888' }}>No staff assigned</span>;
                              return staff.map((st: any) => (
                                <div key={st.stationId} className="staff-item" title={st.stationName}>
                                  <span className="staff-role">{st.stationName}</span>
                                  <span className="staff-name">{st.users.map((u: any) => u.userName).join(", ")}</span>
                                </div>
                              ));
                            })()}
                          </div>
                        </div>

                      </div>
                      <div className="slot-card-actions">
                        <button className="btn-edit-slot" onClick={() => handleEditSlot(slot)}>Edit</button>
                        <button className="btn-delete-slot" onClick={() => handleDeleteClick(slot)}>Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {overrideModalOpen && (
        <OverrideStationModal
          type={overrideType}
          targetDate={overrideDate}
          targetSlot={overrideSlot}
          centerId={station.centerId}
          appointmentClusterId={station.appointmentId}
          effectiveStations={overrideStations}
          onClose={() => setOverrideModalOpen(false)}
          onSuccess={() => {
            setOverrideModalOpen(false);
            fetchConfigData(); // refresh badges
          }}
        />
      )}

      {slotToDelete && (
        <div className="delete-confirm-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="delete-confirm-content">
            <h3>Delete Slot</h3>
            <p>Are you sure you want to delete the slot on <strong>{slotToDelete.date}</strong> from <strong>{slotToDelete.start_time}</strong> to <strong>{slotToDelete.end_time}</strong>?</p>
            <input 
              type="text" 
              className="delete-reason-input" 
              placeholder="Enter reason for deletion..." 
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
            />
            <div className="delete-confirm-actions">
              <button className="btn-cancel" onClick={() => setSlotToDelete(null)} disabled={isDeleting}>Cancel</button>
              <button className="btn-delete-slot" onClick={confirmDelete} disabled={isDeleting || !deleteReason.trim()}>
                {isDeleting ? "Deleting..." : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditClusterSlotsModal;
